import React, { useContext, useState } from 'react';
import { alpha, useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import ButtonBase from '@mui/material/ButtonBase';
import Collapse from '@mui/material/Collapse';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import { CustomThemeContext } from '../../themes/ThemeContext';
import { THEME_CATALOG, ThemeOption, ThemePalettePreview } from '../../themes/themeCatalog';
import { dismissFeature, useIsFeatureNew } from '../../hooks/useIsFeatureNew';
import NewBadge from './NewBadge';

/**
 * A small "mini app" swatch that previews a theme in the Pearsana liquid-glass
 * style: a rainbow / navy canvas gradient, a translucent glass sidebar with a
 * brand-accent active pill, and a pair of glass content cards — finished with a
 * soft drop shadow, inset highlight and a diagonal sheen. Adaptive themes (with
 * `previewAlt`) render a diagonal light/dark split.
 */
const ThemePreview = ({
	preview,
	previewAlt,
	size = 46
}: {
	preview: ThemePalettePreview;
	previewAlt?: ThemePalettePreview;
	size?: number;
}) => {
	const theme = useTheme();
	const pad = size * 0.12;
	const bar = Math.max(2, size * 0.075);

	const composition = (p: ThemePalettePreview) => (
		<Box sx={{ position: 'absolute', inset: 0, display: 'flex', background: p.background }}>
			{/* Glass sidebar */}
			<Box
				sx={{
					width: '38%',
					background: p.sidebar,
					borderRight: `1px solid ${p.border}`,
					display: 'flex',
					flexDirection: 'column',
					gap: `${bar * 0.85}px`,
					p: `${pad * 0.7}px`
				}}
			>
				<Box
					sx={{
						height: bar,
						borderRadius: 99,
						background: `linear-gradient(135deg, ${p.accent}, ${alpha(p.accent, 0.55)})`
					}}
				/>
				<Box sx={{ height: bar * 0.7, borderRadius: 99, bgcolor: p.line, opacity: 0.85 }} />
				<Box sx={{ height: bar * 0.7, width: '68%', borderRadius: 99, bgcolor: p.line, opacity: 0.5 }} />
			</Box>
			{/* Glass content cards */}
			<Box
				sx={{
					flex: 1,
					p: `${pad}px`,
					display: 'flex',
					flexDirection: 'column',
					gap: `${pad * 0.8}px`
				}}
			>
				<Box sx={{ flex: 1, borderRadius: 1, background: p.card, border: `1px solid ${p.border}` }} />
				<Box sx={{ flex: 1, borderRadius: 1, background: p.card, border: `1px solid ${p.border}` }} />
			</Box>
		</Box>
	);

	return (
		<Box
			sx={{
				position: 'relative',
				width: size,
				height: size,
				flexShrink: 0,
				borderRadius: 2,
				overflow: 'hidden',
				border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
				boxShadow: `0 2px 8px ${alpha(theme.palette.common.black, 0.28)}, inset 0 1px 0 ${alpha(
					theme.palette.common.white,
					0.25
				)}`
			}}
		>
			{previewAlt ? (
				<>
					{composition(preview)}
					<Box sx={{ position: 'absolute', inset: 0, clipPath: 'polygon(100% 0, 100% 100%, 0 100%)' }}>
						{composition(previewAlt)}
					</Box>
				</>
			) : (
				composition(preview)
			)}
			{/* Diagonal glass sheen */}
			<Box
				sx={{
					position: 'absolute',
					inset: 0,
					pointerEvents: 'none',
					background: `linear-gradient(135deg, ${alpha('#ffffff', 0.22)} 0%, ${alpha('#ffffff', 0)} 42%)`
				}}
			/>
		</Box>
	);
};

/**
 * Theme picker for the sidebar footer. The list of options is driven entirely by
 * `THEME_CATALOG`, so adding a new theme requires no changes here. Selecting an
 * option persists immediately via the theme context (localStorage-backed).
 *
 * - Expanded: a labelled "Themes" section with selectable preview cards.
 * - Collapsed: a compact vertical stack of preview tiles with tooltips.
 */
const ThemeSelector = ({ collapsed = false }: { collapsed?: boolean }) => {
	const { themeMode, setThemeMode } = useContext(CustomThemeContext);
	const theme = useTheme();
	const [open, setOpen] = useState(false);
	const isNew = useIsFeatureNew('themes');

	const activeOption = THEME_CATALOG.find((o) => o.id === themeMode) ?? THEME_CATALOG[0];

	const renderCard = (option: ThemeOption) => {
		const active = themeMode === option.id;
		return (
			<ButtonBase
				key={option.id}
				onClick={() => setThemeMode(option.id)}
				aria-label={`${option.label} theme`}
				aria-pressed={active}
				sx={{
					width: '100%',
					justifyContent: 'flex-start',
					textAlign: 'left',
					gap: 1.25,
					p: 1,
					borderRadius: 2,
					border: `1px solid ${active ? theme.palette.primary.main : 'transparent'}`,
					bgcolor: active ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
					transition: theme.transitions.create(['background-color', 'border-color']),
					'&:hover': {
						bgcolor: active ? alpha(theme.palette.primary.main, 0.12) : theme.palette.action.hover
					}
				}}
			>
				<ThemePreview preview={option.preview} previewAlt={option.previewAlt} />
				<Box sx={{ flex: 1, minWidth: 0 }}>
					<Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
						{option.label}
					</Typography>
					<Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
						{option.description}
					</Typography>
				</Box>
				<CheckCircleRoundedIcon
					fontSize="small"
					color="primary"
					sx={{ opacity: active ? 1 : 0, transition: theme.transitions.create('opacity') }}
				/>
			</ButtonBase>
		);
	};

	if (collapsed) {
		return (
			<Box
				role="radiogroup"
				aria-label="Theme"
				sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, px: 0.5, py: 1.5 }}
			>
				{THEME_CATALOG.map((option) => {
					const active = themeMode === option.id;
					return (
						<Tooltip key={option.id} title={option.label} placement="right">
							<ButtonBase
								onClick={() => setThemeMode(option.id)}
								role="radio"
								aria-checked={active}
								aria-label={`${option.label} theme`}
								sx={{
									p: 0.5,
									borderRadius: 1.5,
									border: `2px solid ${active ? theme.palette.primary.main : 'transparent'}`,
									transition: theme.transitions.create('border-color')
								}}
							>
								<ThemePreview preview={option.preview} previewAlt={option.previewAlt} size={34} />
							</ButtonBase>
						</Tooltip>
					);
				})}
			</Box>
		);
	}

	return (
		<Box sx={{ px: 1, py: 1, position: 'relative' }}>
			{/* Float the expanded panel above the toggle as an opaque popover so it doesn't
			    bleed into the sidebar nav behind it. */}
			<Collapse
				in={open}
				unmountOnExit
				sx={{ position: 'absolute', left: 8, right: 8, bottom: '100%', zIndex: 20 }}
			>
				<Box
					role="radiogroup"
					aria-label="Theme"
					sx={{
						display: 'flex',
						flexDirection: 'column',
						gap: 0.5,
						mb: 1,
						p: 0.75,
						bgcolor: 'background.paper',
						backgroundImage: 'none',
						backdropFilter: 'none',
						border: `1px solid ${theme.palette.divider}`,
						borderRadius: 2,
						boxShadow: 6
					}}
				>
					{THEME_CATALOG.map(renderCard)}
				</Box>
			</Collapse>
			<ButtonBase
				onClick={() => {
					setOpen((prev) => !prev);
					dismissFeature('themes');
				}}
				aria-expanded={open}
				aria-label="Themes"
				sx={{
					width: '100%',
					justifyContent: 'flex-start',
					gap: 1.25,
					p: 0.75,
					borderRadius: 2,
					transition: theme.transitions.create('background-color'),
					'&:hover': { bgcolor: theme.palette.action.hover }
				}}
			>
				<ThemePreview preview={activeOption.preview} previewAlt={activeOption.previewAlt} size={34} />
				<Box sx={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
						<Typography variant="overline" sx={{ display: 'block', lineHeight: 1.2, opacity: 0.7 }}>
							Themes
						</Typography>
						{isNew && <NewBadge />}
					</Box>
					<Typography variant="body2" noWrap sx={{ fontWeight: 600, lineHeight: 1.3 }}>
						{activeOption.label}
					</Typography>
				</Box>
				<ExpandMoreRoundedIcon
					fontSize="small"
					sx={{
						opacity: 0.7,
						transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
						transition: theme.transitions.create('transform')
					}}
				/>
			</ButtonBase>
		</Box>
	);
};

export default ThemeSelector;
